import { Uuid, HasUuid, UuidMap } from "./uuid";
import { AsyncResult } from "./result";

//
// # Requests
// Or, "RPC over messaging".
//
// Track outstanding requests and execute their callbacks when they come back.
// Type parameter `ResponseType` is the type of the response object.
// The response object must have a `uuid` property, which is used to match the response to the request.
// The request type can be anything, so long as we can identify it by its `uuid`.
//
export class Requests<
  RequestType extends object,
  ResponseType extends HasUuid
> {
  constructor(
    readonly parent: { sendMessage: (_: WithId<RequestType>) => unknown },
    readonly timeout: number = 60 // Timeout in seconds
  ) {}
  //
  private map = new UuidMap<Entry<WithId<ResponseType>>>();

  // Send a request and wait for a response.
  send = async (req_: NoId<RequestType>): AsyncResult<ResponseType, Error> => {
    const uuid = Uuid.new();
    const req: WithId<RequestType> = { uuid, ...req_ };

    // Create our map-entry for `respond` to pick up
    const entry = new Entry<ResponseType>(uuid);
    this.map.insert(entry);

    // Send off the request
    this.parent.sendMessage(req);

    // Wait in an async polling loop until it resolves.
    // Note we check every 100ms, so time out after 10x our `timeout` field specified in seconds.
    for (const _ of Array(10 * this.timeout)) {
      if (entry.state.kind === Kind.Resolved) {
        return AsyncResult.Ok(entry.state.response);
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return AsyncResult.Err({ why: "timeout" });
  };

  // Follow up on a response.
  // Primarily set its state to resolved.
  // Actually picking up its return value will be done by the wait-loop in `send`.
  respond = (response: ResponseType): boolean => {
    const entry = this.map.get(response.uuid);
    if (!entry) return false;
    entry.state = { kind: Kind.Resolved, response };
    return true;
  };
}

class Entry<ResponseType extends HasUuid> {
  constructor(readonly uuid: Uuid) {} // Request & response ID
  state: State<ResponseType> = { kind: Kind.Pending }; // State of the request
}

type State<ResponseType> =
  | { kind: "pending" }
  | { kind: "resolved"; response: ResponseType };

const Kind = { Pending: "pending", Resolved: "resolved" } as const;

export type Error = { why: "timeout" };

// Some type aliasing for making generating IDs internally work
type NoId<T> = Omit<T, "uuid">;
type WithId<T> = NoId<T> & { uuid: Uuid };
